import { Grid } from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';
import { Field, useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import kitService from 'v2/services/kit';

import type { AddressFormKit } from 'v2/interfaces/addressKit';

import { useStyles } from './styles';
import { AutoCompleteFormField } from '../FormFields/AutoCompleteFormField';
import { CheckboxFormField } from '../FormFields/CheckboxFormField';

type FieldOption = {
  label: string;
  value: string;
};

const brStates: FieldOption[] = [
  { label: 'Envio internacional', value: 'EX' },
  { label: 'Acre', value: 'AC' },
  { label: 'Alagoas', value: 'AL' },
  { label: 'Amapá', value: 'AP' },
  { label: 'Amazonas', value: 'AM' },
  { label: 'Bahia', value: 'BA' },
  { label: 'Ceará', value: 'CE' },
  { label: 'Distrito Federal', value: 'DF' },
  { label: 'Espírito Santo', value: 'ES' },
  { label: 'Goiás', value: 'GO' },
  { label: 'Maranhão', value: 'MA' },
  { label: 'Mato Grosso', value: 'MT' },
  { label: 'Mato Grosso do Sul', value: 'MS' },
  { label: 'Minas Gerais', value: 'MG' },
  { label: 'Pará', value: 'PA' },
  { label: 'Paraíba', value: 'PB' },
  { label: 'Paraná', value: 'PR' },
  { label: 'Pernambuco', value: 'PE' },
  { label: 'Piauí', value: 'PI' },
  { label: 'Rio de Janeiro', value: 'RJ' },
  { label: 'Rio Grande do Norte', value: 'RN' },
  { label: 'Rio Grande do Sul', value: 'RS' },
  { label: 'Rondônia', value: 'RO' },
  { label: 'Roraima', value: 'RR' },
  { label: 'Santa Catarina', value: 'SC' },
  { label: 'São Paulo', value: 'SP' },
  { label: 'Sergipe', value: 'SE' },
  { label: 'Tocantins', value: 'TO' },
];

const isInternationalDelivery = (state: string) => state === 'EX';

export function mapCity(city: string, state?: string) {
  if (isInternationalDelivery(state)) return city;

  const renamedCity = city.toLowerCase().replace(/(?:^|\s)\S/g, function (city) {
    return city.toUpperCase();
  });

  return {
    label: renamedCity,
    value: city,
  };
}

const Address = () => {
  const classes = useStyles();

  const { values, setFieldValue } = useFormikContext<AddressFormKit>();
  const getCitiesByState = useQuery<FieldOption[]>(
    ['states', values.state, 'cities'],
    () => kitService.getCitiesByState(values.state).then((result) => result.map(mapCity)),
    {
      enabled: !!values.state,
    }
  );

  useEffect(() => {
    if (values.noNumber) setFieldValue('number', '');
  }, [setFieldValue, values.noNumber]);

  return (
    <>
      <Grid item xs={12} sm={4}>
        <Field
          label='Estado'
          name='state'
          id='state'
          options={brStates}
          component={SelectFormField}
          onChange={() => setFieldValue('city', '')}
          placeholder='Selecione um estado'
        />
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {isInternationalDelivery(values.state) ? (
            <Field label='Cidade' name='city' id='city-text' placeholder='Digite o nome da sua cidade' component={TextFormField} maxLength={40} />
          ) : (
            <Field
              label='Cidade'
              name='city'
              id='city-select'
              options={getCitiesByState.data || []}
              component={AutoCompleteFormField}
              isLoading={getCitiesByState.isLoading}
              placeholder={values.state ? 'Selecione uma cidade' : 'Selecione um estado primeiro'}
              disabled={getCitiesByState.isLoading || !values.state}
              emptyOptionLabel='Selecione uma cidade'
              maxLength={40}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Field
            label={isInternationalDelivery(values.state) ? 'ZIP Code' : 'CEP'}
            name='zipcode'
            id='zipcode'
            mask='zipcode'
            component={TextFormField}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Field label='Endereço' name='street' id='street' component={TextFormField} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Field label='Número' name='number' id='number' component={TextFormField} mask='number' disabled={values.noNumber} />
        </Grid>
        <Grid item xs={12} sm={3} className={classes.noNumberField}>
          <Field label='Sem Número' name='noNumber' id='noNumber' component={CheckboxFormField} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={5}>
          <Field label='Bairro' name='neighborhood' id='neighborhood' component={TextFormField} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Field label='Complemento' name='additionalInfo' id='additionalInfo' component={TextFormField} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Field label='Telefone' name='phone' id='phone' mask={'phone'} component={TextFormField} />
        </Grid>
      </Grid>
    </>
  );
};

export default Address;
